/**
 * Floral Fields B2B Card Stepper & Quick Add to Drawer Engine
 * - Handles quantity stepper [-] [num] [+]
 * - Intercepts all product card add-to-cart clicks and form submits
 * - Performs AJAX cart addition requesting 'cart-drawer' and 'cart-icon-bubble'
 * - Seamlessly synchronizes and slides out Dawn Cart Drawer (#CartDrawer)
 * - Provides live visual feedback (Loading spinner -> ADDED ✓ -> Reset)
 * - Prevents default page refresh and prevents redirect to product page
 */
(function () {
  'use strict';

  function initCardStepper() {
    function executeAddToCart(form, submitBtn) {
      if (!form) return;
      if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') {
        return;
      }

      const variantIdInput = form.querySelector('input[name="id"]');
      const qtyInput = form.querySelector('input[name="quantity"]');
      const variantId = variantIdInput ? variantIdInput.value : null;
      let qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (isNaN(qty) || qty < 1) qty = 1;

      if (!variantId) {
        console.warn('[FloralFields] Missing variant ID for card add form');
        return;
      }

      // Set Loading State
      const btnText = submitBtn ? (submitBtn.querySelector('.ff-card-btn-text') || submitBtn.querySelector('span')) : null;
      const spinner = submitBtn ? submitBtn.querySelector('.loading__spinner') : null;
      const originalText = btnText ? btnText.textContent : 'ADD TO BASKET';

      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');

      // Prepare FormData requesting cart-drawer and cart-icon-bubble sections
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', qty);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      const addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript, application/json'
        },
        body: formData
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (response) {
          if (response.status && response.status >= 400) {
            const errMsg = response.description || response.message || 'Error adding to cart';
            alert(errMsg);
            return;
          }

          // Sync into Cart Drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (typeof cartDrawer.renderContents === 'function' && response.sections) {
              cartDrawer.renderContents(response);
            } else {
              // Fallback manual section update
              if (response.sections) {
                if (response.sections['cart-drawer']) {
                  const parsedDrawer = new DOMParser().parseFromString(response.sections['cart-drawer'], 'text/html');
                  const newDrawerInner = parsedDrawer.querySelector('#CartDrawer');
                  const currentDrawer = document.getElementById('CartDrawer');
                  if (newDrawerInner && currentDrawer) {
                    currentDrawer.innerHTML = newDrawerInner.innerHTML;
                  }
                }
                if (response.sections['cart-icon-bubble']) {
                  const parsedBubble = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
                  const newBubble = parsedBubble.querySelector('#cart-icon-bubble') || parsedBubble.querySelector('.shopify-section');
                  const currentBubble = document.getElementById('cart-icon-bubble');
                  if (newBubble && currentBubble) {
                    currentBubble.innerHTML = newBubble.innerHTML;
                  }
                }
              }
              cartDrawer.classList.remove('is-empty');
              const inner = cartDrawer.querySelector('.drawer__inner');
              if (inner) inner.classList.remove('is-empty');
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open(submitBtn);
              } else {
                cartDrawer.classList.add('animate', 'active');
                document.body.classList.add('overflow-hidden');
              }
            }
          } else {
            // If cart drawer element not yet in DOM, refresh bubble count
            if (response.sections && response.sections['cart-icon-bubble']) {
              const parsedBubble = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
              const newBubble = parsedBubble.querySelector('#cart-icon-bubble') || parsedBubble.querySelector('.shopify-section');
              const currentBubble = document.getElementById('cart-icon-bubble');
              if (newBubble && currentBubble) {
                currentBubble.innerHTML = newBubble.innerHTML;
              }
            }
          }

          // Publish global Dawn cart update event
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'ff-card-stepper',
              productVariantId: variantId,
              cartData: response
            });
          }

          // Visual Success Feedback on Button
          if (btnText) {
            btnText.textContent = 'ADDED ✓';
          }
          if (submitBtn) {
            submitBtn.style.backgroundColor = '#606E4A';
            submitBtn.style.borderColor = '#606E4A';
          }

          setTimeout(function () {
            if (btnText) btnText.textContent = originalText;
            if (submitBtn) {
              submitBtn.style.backgroundColor = '';
              submitBtn.style.borderColor = '';
            }
          }, 1800);
        })
        .catch(function (err) {
          console.error('[FloralFields] Add to cart error:', err);
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
          }
          if (spinner) spinner.classList.add('hidden');
        });
    }

    // 1. Click Handler (Capture Phase) for Steppers and Add-to-Cart Buttons
    document.addEventListener(
      'click',
      function (e) {
        // Minus Button
        const minusBtn = e.target.closest('.ff-stepper-minus');
        if (minusBtn) {
          e.preventDefault();
          e.stopPropagation();
          const stepper = minusBtn.closest('.ff-card-stepper');
          const input = stepper ? stepper.querySelector('.ff-card-stepper-input') : null;
          if (input) {
            let currentVal = parseInt(input.value, 10) || 1;
            if (currentVal > 1) {
              input.value = currentVal - 1;
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          return;
        }

        // Plus Button
        const plusBtn = e.target.closest('.ff-stepper-plus');
        if (plusBtn) {
          e.preventDefault();
          e.stopPropagation();
          const stepper = plusBtn.closest('.ff-card-stepper');
          const input = stepper ? stepper.querySelector('.ff-card-stepper-input') : null;
          if (input) {
            let currentVal = parseInt(input.value, 10) || 1;
            input.value = currentVal + 1;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        // Slider Dots
        const sliderDot = e.target.closest('.ff-card-dot');
        if (sliderDot) {
          e.stopPropagation();
          return;
        }

        // Add to Cart Button on ANY product card (below_info, bottom_overlay, top_right_overlay)
        const submitBtn = e.target.closest('.ff-card-bulk-submit, .ff-card-action-btn, .ff-card-form [type="submit"]');
        if (submitBtn) {
          // If button is a modal opener for options (CHOOSE OPTIONS), let modal opener handle it
          if (submitBtn.closest('modal-opener') || submitBtn.getAttribute('aria-haspopup') === 'dialog' && !submitBtn.closest('form')) {
            e.stopPropagation();
            return;
          }

          const form = submitBtn.closest('.ff-card-form--bulk, .ff-card-form');
          if (form) {
            e.preventDefault();
            e.stopPropagation();
            if (typeof e.stopImmediatePropagation === 'function') {
              e.stopImmediatePropagation();
            }
            executeAddToCart(form, submitBtn);
            return;
          }
        }
      },
      true // capture phase
    );

    // 2. Quantity input validation on blur / change
    document.addEventListener('change', function (e) {
      if (e.target.matches('.ff-card-stepper-input')) {
        let val = parseInt(e.target.value, 10);
        if (isNaN(val) || val < 1) {
          e.target.value = 1;
        }
      }
    });

    // 3. Form Submit Handler (Capture Phase) for Enter key in inputs
    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target.closest('.ff-card-form--bulk, .ff-card-form');
        if (!form) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        const submitBtn = form.querySelector('.ff-card-bulk-submit, [type="submit"], .ff-card-action-btn');
        executeAddToCart(form, submitBtn);
      },
      true // capture phase
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardStepper);
  } else {
    initCardStepper();
  }
})();
